/**
 * `ScenarioDefinitionV0` — the subset of §10.2 that Milestone 1 reads.
 *
 * The full definition (briefing, ROE, MW schedule, VP conditions) and its
 * validator are M7 work. This is deliberately partial and deliberately versioned:
 * M2 and M7 widen the same interface, and `version` carries the migration.
 *
 * Parsing is hand-written rather than Zod because `packages/rules` has zero
 * runtime dependencies (§4) — Zod lives in `packages/protocol`. `parseScenario`
 * is the trust boundary where the `as*Id` casts of `ids.ts` are legal.
 */
import { asAssetId, asAssetTypeId, asMwEventId, asMwModelId, asNetworkId, asRegionId, } from './ids.js';
import { BANDS, CONSOLE_TABS, MW_CATEGORIES, MW_IR_INTENSITIES, SEAT_IDS, TEAM_IDS, } from './enums.js';
import { assetTypeIn, assetTypesOf, ASSET_TYPES } from '../data/asset-types.js';
import { catalogueOf } from '../data/mw-events.js';
import { rangesFromAdjacency } from './region-graph.js';
import { NO_TURN_FLAGS } from './asset.js';
import { newDetection } from './detection.js';
import { emptyKnowledge } from './knowledge.js';
import { DEFAULT_MW_OMISSION, DEFAULT_WORLD_CONFIG, EMPTY_CYBER_STATE, WORLD_SCHEMA_VERSION, emptyEscalationState, } from './world.js';
import { applyMutations } from './mutation.js';
// The reveal planner is engine code, imported here for one call: a scenario may
// schedule a benign launch in turn 1, and there is no turn-0 resolution to plan it.
// It is a pure leaf function over model + data + rng, so this introduces no cycle.
import { planMwReveal } from '../engine/mw-reveal.js';
/**
 * The tab set a seat gets when the scenario says nothing — **the one place it is
 * decided**, so a seat cannot end up with two different defaults in two screens.
 *
 * One rule, three clauses: every seat gets the theatre map and the intel reader,
 * because those are the common picture; the orbital board goes to the cells that
 * act in orbit; and the cyber and missile-warning boards go to their own cells.
 * CREWCOM and the umpire see all five, because commanding is reading (RB §5.1).
 *
 * ## Two rows a reader will stop on. Both are deliberate.
 *
 * **`GM` is unreachable, and is here because the type demands a complete record.**
 * `capabilitiesFor` takes a `Cell`, and `Cell` does not include `GM` — the umpire
 * is served `gm.snapshot` carrying a `GmView`, never a `CellView`, so a GM console
 * is handed no `capabilities` block, draws an empty tab strip and mounts no module
 * at all. Nothing reads this row. It matters because Phase 8 asked for the
 * opposite conclusion — the archive route 403s the GM on purpose, so "a GM seat
 * must not be given the INTEL tab" — and the answer is that it is not given *any*
 * tab, by the shape of the wire rather than by this table. `seat-composition`
 * asserts that, so the day somebody widens `capabilitiesFor` to `SeatId` the
 * unreachability fails loudly instead of quietly handing an umpire a rail of
 * refusals. A scenario *may* author a `seats.GM` entry and validation will accept
 * it; it will have no effect, for the same reason.
 *
 * **`FIRES` has no `MW`, and that is the rule speaking rather than an omission.**
 * RB §2.4 gives Joint Fires the *release*, not the warning board: MW runs the
 * recognition problem and makes the call, FIRES shoots at what MW released. So a
 * released track reaches a FIRES seat as `view.mwReleases`, which is a row in the
 * `INTERCEPT` target picker — and reaches it nowhere else, which is the honest
 * consequence and not an accident. Giving FIRES the board would hand one cell a
 * free check on another cell's work, which is exactly what M4-D7 withholds the
 * classification modifier to prevent.
 */
export const DEFAULT_SEAT_TABS = Object.freeze({
    CREWCOM: Object.freeze(['THEATRE', 'SDA', 'CYBER', 'MW', 'INTEL']),
    SDA: Object.freeze(['THEATRE', 'SDA', 'INTEL']),
    MW: Object.freeze(['THEATRE', 'MW', 'INTEL']),
    OW: Object.freeze(['THEATRE', 'SDA', 'INTEL']),
    EW: Object.freeze(['THEATRE', 'SDA', 'INTEL']),
    CW: Object.freeze(['THEATRE', 'CYBER', 'INTEL']),
    FIRES: Object.freeze(['THEATRE', 'SDA', 'INTEL']),
    ISR: Object.freeze(['THEATRE', 'SDA', 'INTEL']),
    GM: Object.freeze(['THEATRE', 'SDA', 'CYBER', 'MW', 'INTEL']),
});
/**
 * This seat's tabs, in `CONSOLE_TABS` order.
 *
 * Canonical order rather than authoring order, so two scenarios that give a seat
 * the same three modules produce the same tab strip and a reordered document
 * cannot move the UI.
 */
export function seatTabsFor(seat, table) {
    const authored = table?.[seat];
    if (authored === undefined)
        return DEFAULT_SEAT_TABS[seat];
    const chosen = new Set(authored);
    return CONSOLE_TABS.filter((tab) => chosen.has(tab));
}
/** The scenario's seat table, or `undefined` when it authored none. */
export function scenarioSeatTabs(scenario) {
    return scenario.seats;
}
/**
 * The scenario's networks, flattened with the team that declared each one (F4).
 *
 * `WorldState` deliberately does not carry this: a network's *name* and which
 * asset is its router are presentation and topology, not rules, and nothing in
 * `resolveTurn` reads either — `buildInitialWorld` uses `routerHardening` once, to
 * stamp the router asset, and is done with the table. The projection needs it to
 * draw the cyber map, and gets it the same way the side branding reaches the join
 * response: resolved from the scenario document the game was created against.
 */
export function scenarioNetworks(scenario) {
    const out = [];
    for (const oob of scenario.orderOfBattle ?? []) {
        for (const network of oob.networks ?? []) {
            out.push({
                id: network.id,
                name: network.name,
                team: oob.team,
                router: network.router ?? null,
            });
        }
    }
    return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
export const SCENARIO_SIDE_KEYS = ['BLUFOR', 'REDFOR', 'GM'];
/** Long enough for "U.S. Indo-Pacific Command", short enough to hold the layout. */
const SIDE_TEXT_MAX = 60;
const DEFAULTS = {
    signature: 'NORMAL',
    hardening: 'NORMAL',
    capacity: 1,
    vpValue: 0,
};
function deriveInitialRung(template, flags) {
    if (template.initialEnemyRung)
        return template.initialEnemyRung;
    if (flags.includes('COVERT'))
        return 'HIDDEN';
    if (template.deployed === false)
        return 'HIDDEN';
    if (template.segment === 'CYBER')
        return 'HIDDEN';
    if (template.segment === 'GROUND' && (template.mobility ?? 'STATIC') === 'STATIC') {
        return 'FIXED'; // RB §4.4.1 — known infrastructure
    }
    return 'FOUND';
}
/**
 * Flags a template does not state are taken from the asset type (RB §4.3, §4.4.3).
 * A scenario author should not have to restate EMITTER on every radar — and if
 * they did, the one they forgot would silently stop granting the enemy its [+4].
 * An explicit `flags` array overrides the type entirely.
 */
export function effectiveFlags(template, types = BUILT_IN_TYPES) {
    return template.flags ?? assetTypeIn(types, template.typeId)?.typicalFlags ?? [];
}
/**
 * Mobility and damage class are properties of *what the thing is* (RB §4.4.3 is a
 * table by type), so an unstated one comes from the type rather than being absent.
 *
 * This matters more than it looks: `damageClass` defaults to POINT when absent, and
 * POINT means "destroyed on any hit". A satellite control station authored without
 * an explicit damage class would be levelled by the first SRBM instead of going
 * OFFLINE for three turns.
 */
export function effectiveMobility(template, types = BUILT_IN_TYPES) {
    return template.mobility ?? assetTypeIn(types, template.typeId)?.mobility ?? null;
}
export function effectiveDamageClass(template, types = BUILT_IN_TYPES) {
    return template.damageClass ?? assetTypeIn(types, template.typeId)?.damageClass ?? null;
}
/**
 * What this launcher fires. The asset's own statement wins; otherwise the type's,
 * which is how a scenario-declared `DF_26_TEL` fields its round without every
 * launcher in the order of battle repeating it (§10.4.2).
 */
export function effectiveWeaponType(template, types = BUILT_IN_TYPES) {
    return template.weaponType ?? assetTypeIn(types, template.typeId)?.weaponType;
}
function templateToAsset(template, owner, types) {
    const asset = {
        id: asAssetId(template.id),
        owner,
        typeId: asAssetTypeId(template.typeId),
        assignedCell: template.assignedCell,
        segment: template.segment,
        location: template.location,
        signature: template.signature ?? DEFAULTS.signature,
        hardening: template.hardening ?? DEFAULTS.hardening,
        network: asNetworkId(template.network),
        ttcStation: template.ttcStation ? asAssetId(template.ttcStation) : null,
        capacity: template.capacity ?? DEFAULTS.capacity,
        flags: effectiveFlags(template, types),
        vpValue: template.vpValue ?? DEFAULTS.vpValue,
        destroyed: false,
        onMission: template.onMission ?? true,
        turnFlags: NO_TURN_FLAGS,
    };
    // Optional properties are attached conditionally: `exactOptionalPropertyTypes`
    // makes `{ deltaV: undefined }` a different type from `{}`, and the second is
    // what serialises cleanly to JSONB.
    return {
        ...asset,
        ...(template.deltaV !== undefined ? { deltaV: template.deltaV } : {}),
        ...(effectiveMobility(template, types) !== null
            ? { mobility: effectiveMobility(template, types) }
            : {}),
        ...(effectiveDamageClass(template, types) !== null
            ? { damageClass: effectiveDamageClass(template, types) }
            : {}),
        ...(template.ttcCapacity !== undefined ? { ttcCapacity: template.ttcCapacity } : {}),
        ...(template.magazine !== undefined ? { magazine: template.magazine } : {}),
        ...(template.disguisedAs !== undefined
            ? { disguisedAs: asAssetTypeId(template.disguisedAs) }
            : {}),
        ...(template.deployed !== undefined ? { deployed: template.deployed } : {}),
        ...(template.deployParent !== undefined
            ? { deployParent: asAssetId(template.deployParent) }
            : {}),
        ...(template.deployCapacity !== undefined ? { deployCapacity: template.deployCapacity } : {}),
        ...(effectiveWeaponType(template, types) !== undefined
            ? { weaponType: effectiveWeaponType(template, types) }
            : {}),
        ...(template.mwModel !== undefined ? { mwModel: asMwModelId(template.mwModel) } : {}),
    };
}
/**
 * Scheduled benign launches, materialised as one `MwEvent` per receiving team
 * (decision M4-D3).
 *
 * `turn` is the turn during whose window the event is *seen*, and `launchTurn` is
 * stored one behind it — because a launch in turn *n* is classified in turn *n+1*
 * (RB §5.3.3), and using one predicate for both kinds of track is worth more than
 * the field reading literally.
 *
 * A **briefed** team's copy arrives already dismissed and named. Four things follow
 * and all four are wanted: it banks no escalation (a DISMISS is not a
 * release-as-threat), it does not gate the MW seat's readiness, it costs the server
 * no timers, and the *unbriefed* team still gets an ordinary unknown track.
 */
function scheduledMwEvents(scenario) {
    const catalogue = catalogueOf((scenario.mwCatalogue ?? []).map((m) => ({ ...m, id: asMwModelId(m.id) })));
    const events = [];
    let seq = 1;
    for (const scheduled of scenario.scheduledMwEvents ?? []) {
        const model = catalogue.find((m) => m.id === scheduled.model);
        // `parseScenario` rejects an unknown model, so this is defence for a world built
        // from an unvalidated document rather than a case a scenario can reach.
        if (!model)
            continue;
        const impact = scheduled.impactSite ?? scheduled.launchSite;
        for (const team of TEAM_IDS) {
            const briefed = (scheduled.briefedTo ?? []).includes(team);
            const id = asMwEventId(`MW-S${scheduled.turn}-${seq}-${team}`);
            events.push({
                id,
                warnedTeam: team,
                launchTurn: scheduled.turn - 1,
                launchSite: asRegionId(scheduled.launchSite),
                impactSite: asRegionId(impact),
                trueType: model.category,
                trueModel: model.id,
                benign: true,
                weapon: null,
                revealedThroughStage: briefed ? 4 : 0,
                omissions: [],
                // A briefed track needs no constellation to reach its owner: they were told.
                received: true,
                workingIdentity: briefed ? model.id : null,
                briefed,
                release: briefed
                    ? {
                        releasedAtStage: 4,
                        assignedIdentity: 'BENIGN',
                        disposition: 'DISMISS',
                        releasedTurn: scheduled.turn,
                        releasedAtMs: 0,
                    }
                    : null,
            });
        }
        seq += 1;
    }
    return events;
}
function initialDetection(observer, asset, template, rung) {
    const base = newDetection(observer, asset.id, rung);
    const characterized = template.initialEnemyCharacterized ?? false;
    const band = asset.location.kind === 'ORBIT' ? asset.location.band : undefined;
    const slot = asset.location.kind === 'ORBIT' ? asset.location.slot : undefined;
    const region = asset.location.kind === 'GROUND' ? asset.location.region : undefined;
    return {
        ...base,
        characterized,
        ...(band !== undefined ? { knownBand: band } : {}),
        ...(rung !== 'FOUND' && slot !== undefined ? { knownSlot: slot } : {}),
        ...(region !== undefined ? { knownRegion: region } : {}),
        // A scenario-start fix is treated as obtained on turn 0. STATIC ground assets
        // never expire (RB §1.2), so they carry no expiry at all.
        ...(rung === 'FIXED' || rung === 'TRACKED' ? { fixObtainedTurn: 0 } : {}),
    };
}
/** The built-in table, as a list, for the `effective*` helpers' default argument. */
const BUILT_IN_TYPES = Object.freeze(Object.values(ASSET_TYPES));
export function buildInitialWorld(scenario, contentVersion) {
    const assetTypes = assetTypesOf(scenario.assetTypes ?? []);
    const assets = {};
    const knowledge = {
        BLUFOR: emptyKnowledge('BLUFOR'),
        REDFOR: emptyKnowledge('REDFOR'),
    };
    for (const oob of scenario.orderOfBattle) {
        // A network's router hardening overrides the asset's own, so that a scenario
        // can say "the way in is hardened" once rather than per asset (RB §5.6.1).
        const routerHardening = new Map();
        for (const network of oob.networks ?? []) {
            if (network.router && network.routerHardening) {
                routerHardening.set(network.router, network.routerHardening);
            }
        }
        for (const template of oob.assets) {
            const base = templateToAsset(template, oob.team, assetTypes);
            const override = routerHardening.get(template.id);
            const asset = override ? { ...base, hardening: override } : base;
            assets[asset.id] = asset;
            // RB §5.6.1: the defender may or may not know their own hardening. Default
            // is that they do — not knowing your own kit is the exception worth authoring.
            if (template.ownerKnowsHardening !== false) {
                knowledge[oob.team] = {
                    ...knowledge[oob.team],
                    knownHardening: {
                        ...knowledge[oob.team].knownHardening,
                        [asset.id]: asset.hardening,
                    },
                };
            }
            const rung = deriveInitialRung(template, asset.flags);
            if (rung === 'HIDDEN')
                continue;
            const observer = oob.team === 'BLUFOR' ? 'REDFOR' : 'BLUFOR';
            const record = initialDetection(observer, asset, template, rung);
            knowledge[observer] = {
                ...knowledge[observer],
                detections: { ...knowledge[observer].detections, [asset.id]: record },
            };
        }
    }
    const regions = scenario.map.regions.map((r) => ({
        id: asRegionId(r.id),
        name: r.name,
        adjacentTo: r.adjacentTo.map(asRegionId),
    }));
    // Declared or derived, never both (§10.4.1). An authored table is normalised to
    // ascending-id pairs here so that a pair has one spelling in the snapshot however
    // the author happened to write it, and `null` entries are dropped — the absence
    // of a pair *is* how unreachable is stored.
    const ranges = scenario.map.ranges === undefined
        ? rangesFromAdjacency(regions)
        : scenario.map.ranges
            .filter((r) => r.range !== null)
            .map((r) => {
            const from = asRegionId(r.from);
            const to = asRegionId(r.to);
            return from < to
                ? { from, to, range: r.range }
                : { from: to, to: from, range: r.range };
        })
            .sort((a, b) => (a.from === b.from ? (a.to < b.to ? -1 : 1) : a.from < b.from ? -1 : 1));
    const debris = { LEO: 0, MEO: 0, GEO: 0, HEO: 0 };
    for (const band of BANDS) {
        debris[band] = scenario.initialDebris?.[band] ?? 0;
    }
    const weather = {};
    for (const region of regions) {
        weather[region.id] = scenario.initialWeather?.[region.id] ?? 'CLEAR';
    }
    const mwCatalogue = catalogueOf((scenario.mwCatalogue ?? []).map((m) => ({ ...m, id: asMwModelId(m.id) })));
    const world = {
        schemaVersion: WORLD_SCHEMA_VERSION,
        turn: 1,
        contentVersion,
        map: { regions, ranges },
        assets,
        effects: [],
        knowledge,
        debris,
        weather,
        // Turn 1's forecast is turn 1's weather until phase 8 rolls ahead of it. An
        // empty record would read as "no forecast exists" rather than "unknown".
        weatherForecast: { ...weather },
        escalation: emptyEscalationState(scenario.escalation?.start ?? 0),
        cyber: EMPTY_CYBER_STATE,
        weapons: [],
        mwEvents: scheduledMwEvents(scenario),
        mwCatalogue,
        assetTypes,
        roe: scenario.roe ?? [],
        constellationThresholds: scenario.constellationThresholds ?? [],
        config: {
            ...DEFAULT_WORLD_CONFIG,
            ...(scenario.config ?? {}),
            mwOmission: { ...DEFAULT_MW_OMISSION, ...(scenario.config?.mwOmission ?? {}) },
        },
        counters: {
            nextEffectSeq: 1,
            nextContactSeq: 1,
            nextEventSeq: 1,
            nextWeaponSeq: 1,
            nextMwEventSeq: 1,
            nextEscalationSeq: 1,
        },
    };
    // A scenario may schedule a benign launch in turn 1, which no resolution has been
    // run to plan for. Same pure planner, same seed, told to plan the cohort that is
    // live *now* rather than the one this turn created.
    const plans = planMwReveal(world, scenario.gameSeed, 0);
    if (plans.length === 0)
        return world;
    return applyMutations(world, plans.map((plan) => ({
        kind: 'PLAN_MW_REVEAL',
        event: plan.event,
        received: plan.received,
        omissions: plan.omissions,
    })));
}
/**
 * Region checks from §10.3, run before a scenario can be used (§10.4).
 *
 * These are the ones that cost nothing and would otherwise surface mid-exercise
 * as a missile that cannot be fired or a weather lookup that returns `undefined`:
 *
 *  - every region is declared once, with an id and a name;
 *  - ids are `SCREAMING_SNAKE_CASE` (§10.4.2). Region ids are a vocabulary shared
 *    across scenarios, not per-file labels — that is what lets two scenarios in
 *    one theatre share a map pack — so the convention is enforced, not suggested;
 *  - `adjacentTo` names declared regions, never itself, never twice;
 *  - **adjacency is symmetric** — a one-way edge means an SRBM flies one way and
 *    not back, which is never what an author meant;
 *  - every region an asset stands in, or that `initialWeather` names, exists.
 *
 * Returns *every* problem rather than throwing, so the M7 validator and the
 * map-pack tooling (§10.5) can report a whole document at once. `parseScenario`
 * throws on the first, per this module's fail-fast contract.
 *
 * Deliberately **not** an error: a disconnected map. Two theatres no missile can
 * cross between is a legitimate scenario. Use `regionComponents` to report it.
 */
const REGION_ID_PATTERN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;
export function validateScenarioRegions(scenario) {
    const errors = [];
    const regions = scenario.map?.regions;
    if (!Array.isArray(regions) || regions.length === 0) {
        return ['scenario.map.regions: expected a non-empty array'];
    }
    const declared = new Set();
    regions.forEach((region, i) => {
        const at = `scenario.map.regions[${i}]`;
        if (!region || typeof region !== 'object') {
            errors.push(`${at}: expected an object`);
            return;
        }
        if (typeof region.id !== 'string' || region.id.length === 0) {
            errors.push(`${at}.id: expected a non-empty string`);
            return;
        }
        if (!REGION_ID_PATTERN.test(region.id)) {
            errors.push(`${at}.id: ${region.id} is not SCREAMING_SNAKE_CASE — region ids are a vocabulary ` +
                `shared across scenarios (§10.4.2). The display name belongs in .name`);
        }
        if (typeof region.name !== 'string' || region.name.length === 0) {
            errors.push(`${at}.name: expected a non-empty string`);
        }
        if (declared.has(region.id))
            errors.push(`${at}.id: duplicate region id ${region.id}`);
        declared.add(region.id);
    });
    const neighbours = new Map();
    for (const region of regions) {
        if (!region || typeof region.id !== 'string' || region.id.length === 0)
            continue;
        const at = `scenario.map.regions[${region.id}].adjacentTo`;
        if (!Array.isArray(region.adjacentTo)) {
            errors.push(`${at}: expected an array`);
            continue;
        }
        neighbours.set(region.id, region.adjacentTo);
        const seen = new Set();
        for (const neighbour of region.adjacentTo) {
            if (typeof neighbour !== 'string') {
                errors.push(`${at}: expected region ids, found ${typeof neighbour}`);
                continue;
            }
            if (neighbour === region.id)
                errors.push(`${at}: ${region.id} is adjacent to itself`);
            else if (!declared.has(neighbour)) {
                errors.push(`${at}: names ${neighbour}, which is not a declared region`);
            }
            if (seen.has(neighbour))
                errors.push(`${at}: names ${neighbour} twice`);
            seen.add(neighbour);
        }
    }
    for (const [id, adjacentTo] of neighbours) {
        for (const neighbour of adjacentTo) {
            if (typeof neighbour !== 'string' || neighbour === id || !declared.has(neighbour))
                continue;
            const back = neighbours.get(neighbour);
            if (back && !back.includes(id)) {
                errors.push(`scenario.map.regions[${id}].adjacentTo: names ${neighbour}, but ${neighbour} does not ` +
                    `name ${id} — adjacency is symmetric (§10.4)`);
            }
        }
    }
    for (const oob of scenario.orderOfBattle ?? []) {
        for (const asset of oob?.assets ?? []) {
            const location = asset?.location;
            if (location?.kind !== 'GROUND')
                continue;
            if (!declared.has(location.region)) {
                errors.push(`scenario: asset ${asset.id} stands in region ${location.region}, which is not ` +
                    `declared in scenario.map.regions`);
            }
        }
    }
    for (const region of Object.keys(scenario.initialWeather ?? {})) {
        if (!declared.has(region)) {
            errors.push(`scenario.initialWeather: names ${region}, which is not a declared region`);
        }
    }
    errors.push(...validateScenarioRanges(scenario, declared));
    return errors;
}
/**
 * The range table (§10.4.1). Absent is legal and means "derive from adjacency";
 * present means the author has taken responsibility for the whole matrix.
 *
 * The completeness check is the load-bearing one, and it is why the table is
 * all-or-nothing. Under a hop metric a pair the author never thought about still
 * got a defensible number. Under a declared one it gets *nothing*, and nothing
 * reads as unreachable — so a forgotten pair is a missile that silently cannot be
 * fired, which is exactly the failure §10.3 exists to catch before an exercise
 * rather than during one. Declaring the pair `null` is how an author says
 * "unreachable" on purpose, and it satisfies the check.
 */
function validateScenarioRanges(scenario, declared) {
    const ranges = scenario.map?.ranges;
    if (ranges === undefined)
        return [];
    const errors = [];
    if (!Array.isArray(ranges))
        return ['scenario.map.ranges: expected an array'];
    const seen = new Map();
    ranges.forEach((entry, i) => {
        const at = `scenario.map.ranges[${i}]`;
        if (!entry || typeof entry !== 'object') {
            errors.push(`${at}: expected an object`);
            return;
        }
        for (const end of ['from', 'to']) {
            if (typeof entry[end] !== 'string' || !declared.has(entry[end])) {
                errors.push(`${at}.${end}: ${String(entry[end])} is not a declared region`);
            }
        }
        if (entry.from === entry.to) {
            errors.push(`${at}: ${entry.from} to itself — a region is always range 0 from itself`);
        }
        if (entry.range !== null) {
            if (typeof entry.range !== 'number' || !Number.isFinite(entry.range)) {
                errors.push(`${at}.range: expected an integer or null (unreachable)`);
            }
            else if (!Number.isInteger(entry.range)) {
                errors.push(`${at}.range: ${entry.range} is not an integer`);
            }
            else if (entry.range < 0) {
                errors.push(`${at}.range: ${entry.range} is negative`);
            }
        }
        if (typeof entry.from !== 'string' || typeof entry.to !== 'string')
            return;
        // The pair is unordered, so A–B and B–A are the same row said twice. Catching
        // it matters more than it looks: the two could disagree, and whichever landed
        // last would win silently.
        const key = entry.from < entry.to ? `${entry.from} ${entry.to}` : `${entry.to} ${entry.from}`;
        const first = seen.get(key);
        if (first !== undefined) {
            errors.push(`${at}: ${entry.from}–${entry.to} is already declared at scenario.map.ranges[${first}] — ` +
                `range is symmetric, so a pair is declared once (§10.4.1)`);
        }
        else {
            seen.set(key, i);
        }
    });
    const ids = [...declared].sort();
    const missing = [];
    for (let i = 0; i < ids.length; i += 1) {
        for (let j = i + 1; j < ids.length; j += 1) {
            if (!seen.has(`${ids[i]} ${ids[j]}`))
                missing.push(`${ids[i]}–${ids[j]}`);
        }
    }
    if (missing.length > 0) {
        errors.push(`scenario.map.ranges: declares ranges but omits ${missing.length} pair(s) — ` +
            `${missing.slice(0, 5).join(', ')}${missing.length > 5 ? ', …' : ''}. The table is ` +
            `all-or-nothing (§10.4.1): omit it entirely to derive from adjacency, or declare ` +
            `every pair, using null for one that is unreachable at any range`);
    }
    return errors;
}
/**
 * Scenario-declared asset types (§10.4.2).
 *
 * Well-formedness only. The interesting check is `extends`: an unresolvable one
 * inherits from nothing and produces a launcher with no missions, which fails at
 * order time as "this asset cannot do that" — a message pointing at the wrong
 * thing entirely. Catching it here names the actual mistake.
 */
export function validateScenarioAssetTypes(scenario) {
    const issues = [];
    const err = (message) => void issues.push({ severity: 'error', message });
    const warn = (message) => void issues.push({ severity: 'warning', message });
    const authored = scenario.assetTypes ?? [];
    const authoredIds = new Set(authored.map((t) => t?.id));
    const seen = new Set();
    authored.forEach((row, i) => {
        const at = `scenario.assetTypes[${i}]`;
        if (!row || typeof row !== 'object')
            return err(`${at}: expected an object`);
        if (typeof row.id !== 'string' || row.id.length === 0) {
            return err(`${at}.id: expected a non-empty string`);
        }
        if (seen.has(row.id))
            err(`${at}.id: duplicate asset type id ${row.id}`);
        seen.add(row.id);
        if (row.extends !== undefined && ASSET_TYPES[row.extends] === undefined) {
            err(`${at}.extends: ${row.extends} is not a built-in asset type. A scenario type extends a ` +
                `built-in one; it cannot extend another scenario type`);
        }
        if (row.extends === undefined && ASSET_TYPES[row.id] === undefined && !row.missions?.length) {
            warn(`${at}: ${row.id} extends nothing and declares no missions, so no asset of this type ` +
                `will be able to do anything. Did you mean to set extends?`);
        }
        if (row.maxRange !== undefined) {
            if (!Number.isInteger(row.maxRange) || row.maxRange < 0) {
                err(`${at}.maxRange: expected a non-negative integer`);
            }
            if (row.weaponType === undefined) {
                const inherited = ASSET_TYPES[row.extends ?? row.id]?.weaponType;
                if (inherited === undefined) {
                    warn(`${at}: ${row.id} sets maxRange but names no weaponType, and inherits none — the ` +
                        `range will never be consulted because nothing fires`);
                }
            }
        }
    });
    for (const oob of scenario.orderOfBattle ?? []) {
        for (const asset of oob?.assets ?? []) {
            const typeId = asset?.typeId;
            if (typeof typeId !== 'string')
                continue;
            if (ASSET_TYPES[typeId] === undefined && !authoredIds.has(typeId)) {
                err(`scenario: asset ${asset.id} is of type ${typeId}, which is neither a built-in asset ` +
                    `type nor declared in scenario.assetTypes`);
            }
        }
    }
    return issues;
}
/**
 * Checks on the Missile Warning classification catalogue and schedule (§10.3).
 *
 * The catalogue is scenario content (M4-D2), so there is no single committed table
 * a reviewer can read — which means the *quality* checks a data-table test would
 * have carried have to live here, run against every scenario. Three of them are
 * about whether the table poses a fair puzzle rather than whether it is well-formed:
 *
 *  - **Two models in different categories with identical observables at all four
 *    stages is an error.** No amount of waiting distinguishes them, so a player who
 *    does everything right still guesses — and will read that as a bug.
 *  - **No within-category ambiguity through stage 2 is a warning.** A table where
 *    every model resolves at stage 1 makes releasing at stage 1 free, and RB
 *    §5.3.6's whole price-of-waiting structure stops meaning anything.
 *  - **A catalogue containing only models the order of battle fields is a warning.**
 *    That is an order of battle, not a reference table: a table listing exactly what
 *    the enemy owns tells the other team what the enemy owns. RB §5.3.5's table
 *    lists "all possible events", and it should be authored as a superset.
 *
 * Returns every issue rather than throwing, so the M7 validator can report a whole
 * document at once. `parseScenario` throws on the first *error* and ignores warnings.
 */
/**
 * A `[min, max]` band of non-negative integers. Used for the catalogue's numeric
 * columns, where a reversed pair reads as "matches nothing" to a filter engine
 * rather than as an error, which is exactly the sort of defect §10.3 exists for.
 */
function bandErrors(at, value) {
    if (!Array.isArray(value) || value.length !== 2)
        return [`${at}: expected [min, max]`];
    const [min, max] = value;
    const errors = [];
    for (const [label, n] of [
        ['min', min],
        ['max', max],
    ]) {
        if (typeof n !== 'number' || !Number.isInteger(n) || n < 0) {
            errors.push(`${at}: ${label} ${String(n)} is not a non-negative integer`);
        }
    }
    if (errors.length === 0 && min > max) {
        errors.push(`${at}: min ${String(min)} exceeds max ${String(max)}`);
    }
    return errors;
}
export function validateScenarioMwCatalogue(scenario) {
    const issues = [];
    const err = (message) => void issues.push({ severity: 'error', message });
    const warn = (message) => void issues.push({ severity: 'warning', message });
    const authored = scenario.mwCatalogue ?? [];
    const seenIds = new Set();
    authored.forEach((model, i) => {
        const at = `scenario.mwCatalogue[${i}]`;
        if (!model || typeof model !== 'object')
            return err(`${at}: expected an object`);
        if (typeof model.id !== 'string' || model.id.length === 0) {
            return err(`${at}.id: expected a non-empty string`);
        }
        if (seenIds.has(model.id))
            err(`${at}.id: duplicate model id ${model.id}`);
        seenIds.add(model.id);
        if (typeof model.displayName !== 'string' || model.displayName.length === 0) {
            err(`${at}.displayName: expected a non-empty string`);
        }
        if (!MW_CATEGORIES.includes(model.category)) {
            err(`${at}.category: ${String(model.category)} is not a missile warning category`);
        }
        const o = model.observables;
        if (!o || typeof o !== 'object')
            return err(`${at}.observables: expected an object`);
        if (!MW_IR_INTENSITIES.includes(o.irIntensity)) {
            err(`${at}.observables.irIntensity: expected one of ${MW_IR_INTENSITIES.join(', ')}`);
        }
        const boost = o.boostDurationS;
        if (!Array.isArray(boost) || boost.length !== 2 || boost.some((n) => typeof n !== 'number')) {
            err(`${at}.observables.boostDurationS: expected [min, max] seconds`);
        }
        else if (boost[0] > boost[1]) {
            err(`${at}.observables.boostDurationS: min ${boost[0]} exceeds max ${boost[1]}`);
        }
        if (typeof o.stagingEvents !== 'number' || o.stagingEvents < 0) {
            err(`${at}.observables.stagingEvents: expected a non-negative number`);
        }
        for (const field of ['apogee', 'groundTrack', 'terminal']) {
            if (typeof o[field] !== 'string' || o[field].length === 0) {
                err(`${at}.observables.${field}: expected a non-empty string`);
            }
        }
        // The numeric columns (F3). `apogeeKm` may be null — a row with no ballistic
        // apogee is a real state — but it may not be *absent*: an omitted band is a
        // row the recognition table's filter engine silently cannot judge.
        if (o.apogeeKm === undefined) {
            err(`${at}.observables.apogeeKm: expected [minKm, maxKm], or null for a row with no ` +
                `ballistic apogee. The prose stays the displayed cell; this is what filters`);
        }
        else if (o.apogeeKm !== null) {
            for (const message of bandErrors(`${at}.observables.apogeeKm`, o.apogeeKm))
                err(message);
        }
        for (const message of bandErrors(`${at}.observables.rv`, o.rv))
            err(message);
    });
    const catalogue = catalogueOf(authored.map((m) => ({ ...m, id: asMwModelId(m.id) })));
    // Every model a launcher or a scheduled event names must exist, or a launch
    // produces a track nothing in the reference table can match.
    for (const oob of scenario.orderOfBattle ?? []) {
        for (const asset of oob?.assets ?? []) {
            if (asset?.mwModel === undefined)
                continue;
            if (!catalogue.some((m) => m.id === asset.mwModel)) {
                err(`scenario: asset ${asset.id} names mwModel ${asset.mwModel}, which is not in the catalogue`);
            }
        }
    }
    const declaredRegions = new Set((scenario.map?.regions ?? []).map((r) => r?.id));
    (scenario.scheduledMwEvents ?? []).forEach((event, i) => {
        const at = `scenario.scheduledMwEvents[${i}]`;
        if (typeof event?.turn !== 'number' || event.turn < 1) {
            err(`${at}.turn: expected a turn number of 1 or more`);
        }
        else if (scenario.turns?.max !== undefined && event.turn > scenario.turns.max) {
            err(`${at}.turn: ${event.turn} is past the scenario's last turn (${scenario.turns.max})`);
        }
        if (!declaredRegions.has(event?.launchSite)) {
            err(`${at}.launchSite: ${String(event?.launchSite)} is not a declared region`);
        }
        if (event?.impactSite !== undefined && !declaredRegions.has(event.impactSite)) {
            err(`${at}.impactSite: ${event.impactSite} is not a declared region`);
        }
        if (!catalogue.some((m) => m.id === event?.model)) {
            err(`${at}.model: ${String(event?.model)} is not in the catalogue`);
        }
        for (const team of event?.briefedTo ?? []) {
            if (!TEAM_IDS.includes(team))
                err(`${at}.briefedTo: unknown team ${String(team)}`);
        }
    });
    if (authored.length === 0)
        return issues;
    // Solvability. Only the authored rows are judged: a scenario is not answerable for
    // the seven generic rows it did not write, and merging them in would make every
    // authored table look ambiguous against a fallback nobody fields.
    // Over what the *observation stream* delivers, which is the prose cells plus the
    // reentry-vehicle count (Q10). `apogeeKm` is deliberately absent: it is a second
    // spelling of `apogee`, never its own observable, and including it would let two
    // rows with the same displayed cells pass because their bands differed.
    const fingerprint = (m, throughStage) => {
        const o = m.observables;
        const head = `${o.irIntensity}|${o.boostDurationS?.join('-')}|${o.stagingEvents}`;
        return throughStage === 2
            ? head
            : `${head}|${o.apogee}|${o.groundTrack}|${o.terminal}|${o.rv?.join('-')}`;
    };
    for (let i = 0; i < authored.length; i += 1) {
        for (let j = i + 1; j < authored.length; j += 1) {
            const a = authored[i];
            const b = authored[j];
            if (a.category === b.category)
                continue;
            if (fingerprint(a, 4) === fingerprint(b, 4)) {
                err(`scenario.mwCatalogue: ${a.id} (${a.category}) and ${b.id} (${b.category}) are ` +
                    `indistinguishable through stage 4 — no amount of waiting separates them, so a ` +
                    `release against either is a guess (RB §5.3.5)`);
            }
        }
    }
    const ambiguousEarly = authored.some((a) => authored.some((b) => a !== b && a.category === b.category && fingerprint(a, 2) === fingerprint(b, 2)));
    if (!ambiguousEarly) {
        warn('scenario.mwCatalogue: every model is distinguishable by stage 2, so releasing early ' +
            'is free and RB §5.3.6’s price-of-waiting never applies. Consider two models in ' +
            'one category that share an IR intensity and a boost profile.');
    }
    const fielded = new Set();
    for (const oob of scenario.orderOfBattle ?? []) {
        for (const asset of oob?.assets ?? [])
            if (asset?.mwModel)
                fielded.add(asset.mwModel);
    }
    for (const event of scenario.scheduledMwEvents ?? [])
        if (event?.model)
            fielded.add(event.model);
    if (fielded.size > 0 && authored.every((m) => fielded.has(m.id))) {
        warn('scenario.mwCatalogue: every authored model is one this scenario actually fields. A ' +
            'reference table lists all possible events (RB §5.3.5); one that lists exactly what ' +
            'the enemy owns tells the other team what the enemy owns.');
    }
    return issues;
}
/**
 * Narrowing parse of an untrusted scenario document. Throws with a path-prefixed
 * message on the first problem — scenarios are authored, not user-submitted, so
 * fail fast beats accumulating errors.
 *
 * This does **not** perform the full semantic validation of §10.3 (TT&C cycles,
 * duplicate start slots, unreachable weapon ranges). That is the M7 validator.
 * The region subset (§10.4) runs here because M3's weapon ranges are BFS over the
 * adjacency graph, and a graph defect would otherwise surface as a missile that
 * silently cannot be fired.
 */
/**
 * `sides` is presentation, so a mistake here can never change a resolution — but
 * it can put an unbounded string through three screens, and a typo'd side key is
 * silently no branding at all. Both are worth a clear error at load.
 */
function validateScenarioSides(sides) {
    if (sides === undefined)
        return;
    if (typeof sides !== 'object' || sides === null || Array.isArray(sides)) {
        throw new Error('scenario.sides: expected an object keyed by BLUFOR, REDFOR or GM');
    }
    for (const [key, value] of Object.entries(sides)) {
        if (!SCENARIO_SIDE_KEYS.includes(key)) {
            throw new Error(`scenario.sides: unknown side ${key} (expected one of ${SCENARIO_SIDE_KEYS.join(', ')})`);
        }
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            throw new Error(`scenario.sides.${key}: expected an object`);
        }
        for (const field of ['name', 'identity']) {
            const text = value[field];
            if (text === undefined)
                continue;
            if (typeof text !== 'string' || text.trim().length === 0) {
                throw new Error(`scenario.sides.${key}.${field}: expected a non-empty string`);
            }
            if (text.length > SIDE_TEXT_MAX) {
                throw new Error(`scenario.sides.${key}.${field}: at most ${SIDE_TEXT_MAX} characters (it has ${text.length})`);
            }
        }
        const cells = value.cells;
        if (cells === undefined)
            continue;
        if (typeof cells !== 'object' || cells === null || Array.isArray(cells)) {
            throw new Error(`scenario.sides.${key}.cells: expected an object keyed by seat id`);
        }
        for (const [cell, unit] of Object.entries(cells)) {
            // A typo'd seat id is silently no unit at all, and the patch filename
            // derived from it would never be looked for either. Say so at load.
            if (!SEAT_IDS.includes(cell)) {
                throw new Error(`scenario.sides.${key}.cells: unknown seat ${cell} (expected one of ${SEAT_IDS.join(', ')})`);
            }
            // GM is the umpire's own seat and the only one the GM side has; a player
            // side listing it, or the GM side listing a player cell, is a mistake
            // worth catching rather than quietly ignoring.
            const gmSide = key === 'GM';
            if (gmSide !== (cell === 'GM')) {
                throw new Error(`scenario.sides.${key}.cells: ${cell} does not belong to ${key} — the GM side has only GM, and the player sides have everything but`);
            }
            if (typeof unit !== 'string' || unit.trim().length === 0) {
                throw new Error(`scenario.sides.${key}.cells.${cell}: expected a non-empty string`);
            }
            if (unit.length > SIDE_TEXT_MAX) {
                throw new Error(`scenario.sides.${key}.cells.${cell}: at most ${SIDE_TEXT_MAX} characters (it has ${unit.length})`);
            }
        }
    }
}
/**
 * The seat → tabs block (Q15).
 *
 * A typo'd seat id or tab name is silently *fewer modules on somebody's screen*,
 * which nobody notices until the exercise is running and the missile warning seat
 * has no board. Both are named vocabularies, so both are checked at load.
 *
 * An explicitly empty array is legal and means exactly what it says: a seat with
 * no modules. That is a strange thing to author and a legitimate one — a scenario
 * may seat an observer — so it is not an error.
 */
function validateScenarioSeats(seats) {
    if (seats === undefined)
        return;
    if (typeof seats !== 'object' || seats === null || Array.isArray(seats)) {
        throw new Error('scenario.seats: expected an object keyed by seat id');
    }
    for (const [seat, tabs] of Object.entries(seats)) {
        if (!SEAT_IDS.includes(seat)) {
            throw new Error(`scenario.seats: unknown seat ${seat} (expected one of ${SEAT_IDS.join(', ')})`);
        }
        if (!Array.isArray(tabs)) {
            throw new Error(`scenario.seats.${seat}: expected an array of console tabs`);
        }
        const seen = new Set();
        for (const tab of tabs) {
            if (typeof tab !== 'string' || !CONSOLE_TABS.includes(tab)) {
                throw new Error(`scenario.seats.${seat}: unknown tab ${String(tab)} ` +
                    `(expected one of ${CONSOLE_TABS.join(', ')})`);
            }
            if (seen.has(tab))
                throw new Error(`scenario.seats.${seat}: names ${tab} twice`);
            seen.add(tab);
        }
    }
}
export function parseScenario(input) {
    const doc = input;
    if (!doc || typeof doc !== 'object')
        throw new Error('scenario: not an object');
    if (typeof doc.name !== 'string')
        throw new Error('scenario.name: expected string');
    if (typeof doc.version !== 'number')
        throw new Error('scenario.version: expected number');
    if (typeof doc.gameSeed !== 'string' || doc.gameSeed.length === 0) {
        throw new Error('scenario.gameSeed: expected non-empty string');
    }
    if (!doc.map || !Array.isArray(doc.map.regions)) {
        throw new Error('scenario.map.regions: expected array');
    }
    if (!doc.turns || typeof doc.turns.max !== 'number') {
        throw new Error('scenario.turns.max: expected number');
    }
    if (!Array.isArray(doc.orderOfBattle) || doc.orderOfBattle.length === 0) {
        throw new Error('scenario.orderOfBattle: expected non-empty array');
    }
    const seen = new Set();
    for (const oob of doc.orderOfBattle) {
        if (!TEAM_IDS.includes(oob.team)) {
            throw new Error(`scenario.orderOfBattle: unknown team ${String(oob.team)}`);
        }
        for (const asset of oob.assets) {
            if (seen.has(asset.id))
                throw new Error(`scenario: duplicate asset id ${asset.id}`);
            seen.add(asset.id);
            if (asset.flags?.includes('DISGUISED') && !asset.disguisedAs) {
                throw new Error(`scenario: ${asset.id} is DISGUISED but declares no disguisedAs (§10.3)`);
            }
        }
    }
    validateScenarioSides(doc.sides);
    validateScenarioSeats(doc.seats);
    const regionErrors = validateScenarioRegions(doc);
    const firstRegionError = regionErrors[0];
    if (firstRegionError !== undefined)
        throw new Error(firstRegionError);
    // Warnings are for the M7 validator and the author, not for the loader: a table
    // that is merely boring should still run.
    const typeError = validateScenarioAssetTypes(doc).find((issue) => issue.severity === 'error');
    if (typeError !== undefined)
        throw new Error(typeError.message);
    const mwError = validateScenarioMwCatalogue(doc).find((issue) => issue.severity === 'error');
    if (mwError !== undefined)
        throw new Error(mwError.message);
    return doc;
}
//# sourceMappingURL=scenario.js.map